import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { X, Maximize2, Volume2, VolumeX, Loader2, AlertTriangle } from "lucide-react";
import { streamUrl } from "../lib/api";

export default function VideoPlayer({ channel, onClose, epg }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | playing | error
  const [reason, setReason] = useState("");
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (!channel) return;
    const video = videoRef.current;
    const src = streamUrl(channel.url);
    setStatus("loading");
    setReason("");

    const cleanup = () => {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
    cleanup();

    const onPlaying = () => setStatus("playing");
    video.addEventListener("playing", onPlaying);

    const isM3U8 = channel.url.toLowerCase().split("?")[0].match(/\.(m3u8|m3u)$/) || true;

    if (Hls.isSupported() && isM3U8) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false, maxBufferLength: 30 });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
      hls.on(Hls.Events.ERROR, (_e, data) => {
        // The proxy answers 4xx/5xx with {"detail": "..."} when a channel is offline
        // or cannot be fetched at all. Retrying that is pointless, so surface the
        // reason immediately instead of spinning until the timeout.
        const code = data?.response?.code;
        if (code >= 400) {
          let detail = "";
          try { detail = JSON.parse(data.response.text || "{}").detail || ""; } catch {}
          setReason(detail);
          setStatus("error");
          cleanup();
          return;
        }
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
          else { setStatus("error"); cleanup(); }
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.play().catch(() => {});
    } else {
      video.src = src;
      video.play().catch(() => setStatus("error"));
    }

    const timeout = setTimeout(() => {
      setStatus((s) => (s === "loading" ? "error" : s));
    }, 15000);

    return () => {
      clearTimeout(timeout);
      video.removeEventListener("playing", onPlaying);
      cleanup();
    };
  }, [channel]);

  const toggleMute = () => {
    const v = videoRef.current;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const fullscreen = () => {
    const v = videoRef.current;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  if (!channel) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn" data-testid="video-player-modal">
      <div className="w-full max-w-5xl card overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-tv-border bg-tv-darkest">
          <div className="flex items-center gap-3 min-w-0">
            {channel.logo ? (
              <img src={channel.logo} alt="" className="w-9 h-9 rounded-lg object-contain bg-tv-medium p-1" onError={(e) => (e.target.style.display = "none")} />
            ) : null}
            <div className="min-w-0">
              <div className="font-bold text-tv-text truncate flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-tv-red animate-pulseDot" /> {channel.name}
              </div>
              <div className="text-xs text-tv-muted truncate">{channel.group} {channel.country ? `· ${channel.country}` : ""}</div>
              {epg?.now?.title && (
                <div className="text-xs text-tv-teal truncate mt-0.5" data-testid="player-epg-now">
                  Now: {epg.now.title}
                  {epg?.next?.title ? <span className="text-tv-muted"> · Next: {epg.next.title}</span> : null}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleMute} className="btn-ghost p-2" data-testid="player-mute-button">{muted ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
            <button onClick={fullscreen} className="btn-ghost p-2" data-testid="player-fullscreen-button"><Maximize2 size={16} /></button>
            <button onClick={onClose} className="btn-ghost p-2 hover:!text-tv-red hover:!border-tv-red/40" data-testid="player-close-button"><X size={16} /></button>
          </div>
        </div>

        {/* Video */}
        <div className="relative bg-black aspect-video">
          <video ref={videoRef} controls autoPlay playsInline className="w-full h-full" data-testid="video-element" />
          {status === "loading" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-tv-sub gap-3 pointer-events-none">
              <Loader2 size={30} className="animate-spin text-tv-teal" />
              <span className="text-sm">Connecting to stream…</span>
            </div>
          )}
          {status === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center gap-3 px-6" data-testid="player-error">
              <AlertTriangle size={30} className="text-tv-orange" />
              <div className="text-tv-text font-semibold">This stream could not be played</div>
              <div className="text-tv-muted text-sm max-w-md" data-testid="player-error-reason">
                {reason || "The channel may be offline or geo-restricted. Try another channel."}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
